import React, { useState, useEffect, useCallback } from "react";
import { ContactsAPI } from "@/lib/api";
import { Contact } from "@/types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { toast } from "sonner";
import { Filter, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useDebounce } from "use-debounce";

const contactReasons = {
  general: "General Inquiry",
  advertising: "Advertising & Partnerships",
  feature_request: "Feature Request",
  report: "Report an Issue",
  support: "Technical Support",
  feedback: "Feedback & Suggestions",
  press: "Press & Media",
  business: "Business Development",
  other: "Other",
};

const ContactsPage = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [subject, setSubject] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const limit = 30;

  const fetchContacts = async () => {
    setIsLoading(true);
    try {
      const { data, count } = await ContactsAPI.getAll(
        page,
        limit,
        subject,
        debouncedSearchQuery || undefined
      );
      setContacts(data);
      setTotal(count);
    } catch (error) {
      toast.error("Failed to fetch contacts");
      setContacts([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(1); // Reset to first page on new search
  };

  // Effect for search, subject, and page changes
  useEffect(() => {
    fetchContacts();
  }, [page, subject, debouncedSearchQuery]);

  const handleUpdateContact = async (contact: Contact) => {
    setIsProcessing(true);
    try {
      await ContactsAPI.update(contact.id, {
        admin_notes: adminNotes,
        is_contacted: contact.is_contacted,
        is_emailed: contact.is_emailed,
        is_called: contact.is_called,
        contacted_at: new Date().toISOString(),
      });
      setSelectedContact(null);
      fetchContacts();
      toast.success("Contact updated successfully");
    } catch (error) {
      toast.error("Failed to update contact");
    } finally {
      setIsProcessing(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  // Add validation function
  const isFormValid = (contact: Contact, notes: string): boolean => {
    // Require either emailed or called (or both) AND admin notes
    return (contact.is_emailed || contact.is_called) && notes.trim().length > 0;
  };

  // Update the contact state handler to also set is_contacted
  const handleContactStatusChange = (
    field: "is_emailed" | "is_called",
    checked: boolean
  ) => {
    setSelectedContact((prev) => {
      if (!prev) return null;
      const updates = {
        ...prev,
        [field]: checked,
        // Set is_contacted to true if either is_emailed or is_called is true
        is_contacted:
          field === "is_emailed"
            ? checked || prev.is_called
            : prev.is_emailed || checked,
      };
      return updates;
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Contact Management</h1>
        <p className="text-muted-foreground">
          Manage and respond to contact form submissions
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex flex-1 items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name..."
            className="max-w-sm"
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select
            value={subject || "all"}
            onValueChange={(value) =>
              setSubject(value === "all" ? undefined : value)
            }
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by subject" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Subjects</SelectItem>
              {Object.entries(contactReasons).map(([key, value]) => (
                <SelectItem key={key} value={key}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="h-6 w-24 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-32 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-48 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-32 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-24 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="h-8 w-24 animate-pulse rounded bg-muted ml-auto" />
                  </TableCell>
                </TableRow>
              ))
            ) : contacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  No contacts found
                </TableCell>
              </TableRow>
            ) : (
              contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell>
                    {format(new Date(contact.created_date), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell>{contact.name}</TableCell>
                  <TableCell>{contact.email}</TableCell>
                  <TableCell>
                    {
                      contactReasons[
                        contact.subject as keyof typeof contactReasons
                      ]
                    }
                  </TableCell>
                  <TableCell>
                    <span
                      className={`rounded-full px-2 py-1 text-xs ${
                        contact.is_contacted
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {contact.is_contacted ? "Contacted" : "Pending"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedContact(contact);
                        setAdminNotes(contact.admin_notes || "");
                      }}
                    >
                      Manage
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)}{" "}
            of {total} results
          </p>
          <Pagination className="mt-4">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (page > 1) setPage(page - 1);
                  }}
                  className={page === 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum = page;
                if (page < 3) {
                  pageNum = i + 1;
                } else if (page > totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }

                if (pageNum > 0 && pageNum <= totalPages) {
                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setPage(pageNum);
                        }}
                        isActive={pageNum === page}
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                }
                return null;
              })}
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (page < totalPages) setPage(page + 1);
                  }}
                  className={
                    page === totalPages ? "pointer-events-none opacity-50" : ""
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <Dialog
        open={!!selectedContact}
        onOpenChange={() => setSelectedContact(null)}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Manage Contact</DialogTitle>
            <DialogDescription>
              Update contact status and add admin notes. You must select at
              least one contact method and provide admin notes.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="emailed"
                  checked={selectedContact?.is_emailed}
                  onCheckedChange={(checked) =>
                    handleContactStatusChange("is_emailed", !!checked)
                  }
                />
                <label htmlFor="emailed">Emailed</label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="called"
                  checked={selectedContact?.is_called}
                  onCheckedChange={(checked) =>
                    handleContactStatusChange("is_called", !!checked)
                  }
                />
                <label htmlFor="called">Called</label>
              </div>
            </div>
            <div className="space-y-2">
              <Textarea
                placeholder="Admin notes (required)..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className={
                  adminNotes.trim().length === 0 ? "border-red-500" : ""
                }
              />
              {adminNotes.trim().length === 0 && (
                <p className="text-sm text-red-500">Admin notes are required</p>
              )}
            </div>
            {!selectedContact?.is_emailed && !selectedContact?.is_called && (
              <p className="text-sm text-red-500">
                Please select at least one contact method
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSelectedContact(null)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                selectedContact && handleUpdateContact(selectedContact)
              }
              disabled={
                isProcessing ||
                !selectedContact ||
                !isFormValid(selectedContact, adminNotes)
              }
            >
              {isProcessing ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactsPage;
